import { useEffect, useMemo, useState } from 'react';
import { Code, FileUp, LogIn, ShieldCheck, Sparkles } from 'lucide-react';
import { analyze, createEmptyProfile } from './core/analyze';
import type { AudienceMode, ProfileInput, Report, SectionId } from './core/types';
import { Button, ButtonLink } from './components/ui/Button';
import { Card, CardBody } from './components/ui/Card';
import { ThemeToggle } from './components/ui/ThemeToggle';
import { ToastProvider } from './components/ui/Toast';
import { useToast } from './components/ui/toastContext';
import { HeroPreview } from './features/analyzer/HeroPreview';
import { ProfileForm } from './features/analyzer/ProfileForm';
import { ReportPanel } from './features/analyzer/ReportPanel';
import { ScoreChip } from './features/analyzer/ScoreChip';
import { SectionNav } from './features/analyzer/SectionNav';
import { ImportDialog } from './features/import/ImportDialog';
import { fetchBackendConfig, type BackendConfig } from './lib/api';
import { readAuthReturn, type AuthReturn } from './lib/authReturn';
import { APP_NAME, DISCLAIMER, REPO_URL } from './lib/constants';
import { sanitiseMode, sanitiseProfile, withUniqueIds } from './lib/profileCodec';
import { decodeShareLink } from './lib/shareLink';
import { STORAGE_KEYS, clearAll, readJson, writeJson } from './lib/storage';
import { useDebouncedEffect, useDebouncedValue } from './hooks/useDebouncedValue';

/**
 * The application shell.
 *
 * All the domain logic lives in `src/core/`, all the rendering in
 * `src/features/`. What is left here is the wiring the rest deliberately does not
 * do: where state comes from on first paint, when it gets written back, and how
 * the two panels talk to each other.
 */

/** Anything at all typed in? Drives whether we show the hero or the score. */
function hasContent(profile: ProfileInput): boolean {
  return (
    profile.headline.trim() !== '' ||
    profile.about.trim() !== '' ||
    profile.skills.length > 0 ||
    profile.experience.some((entry) => entry.title || entry.organisation || entry.description)
  );
}

/**
 * Resolves the profile to start from, in priority order:
 * share link → localStorage → empty.
 *
 * A share link wins because following one is an explicit request to see *that*
 * profile; falling back to the visitor's own saved draft would be baffling.
 */
function initialState(): {
  profile: ProfileInput;
  mode: AudienceMode;
  fromLink: boolean;
  auth: AuthReturn | null;
} {
  // The optional sign-in comes back through the same fragment, so it is read
  // here too — one place that decides what the URL means.
  const auth = readAuthReturn(window.location.hash);
  const shared = decodeShareLink(window.location.hash);
  if (shared) return { profile: shared.profile, mode: shared.mode, fromLink: true, auth };

  return {
    // Both values are run through the sanitisers: what comes out of storage was
    // written by an older version of this app and cannot be trusted to match the
    // current schema.
    profile: withUniqueIds(sanitiseProfile(readJson<unknown>(STORAGE_KEYS.profile, null))),
    mode: sanitiseMode(readJson<unknown>(STORAGE_KEYS.mode, null)),
    fromLink: false,
    auth,
  };
}

export default function App() {
  return (
    <ToastProvider>
      <AppShell />
    </ToastProvider>
  );
}

function AppShell() {
  const toast = useToast();
  // A lazy `useState` initialiser rather than a ref: this has to run exactly
  // once, and it has to be readable during the first render.
  const [boot] = useState(initialState);

  const [profile, setProfile] = useState<ProfileInput>(boot.profile);
  const [mode, setMode] = useState<AudienceMode>(boot.mode);
  const [importOpen, setImportOpen] = useState(false);
  const [backend, setBackend] = useState<BackendConfig>({ oauthEnabled: false, authUrl: null });
  const [signedInAs] = useState<string | null>(
    boot.auth?.kind === 'identity' ? boot.auth.name : null
  );

  /* ------------------------------------------------------- fragment intake --- */
  useEffect(() => {
    if (!boot.fromLink && !boot.auth) return;
    // Drop the payload from the address bar once it is loaded. Otherwise a
    // refresh silently reverts every edit the visitor has just made, and the
    // whole profile stays visible in the URL bar over their shoulder.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    if (boot.fromLink) {
      toast.success(
        'Loaded a shared profile. Editing it here changes nothing for whoever sent it.'
      );
    }
    if (boot.auth?.kind === 'error') toast.error(boot.auth.message);
    if (boot.auth?.kind === 'identity') {
      // Said plainly, because people reasonably expect signing in to fill the
      // form. LinkedIn's OIDC scopes simply do not include profile text.
      toast.success(
        'Signed in. LinkedIn only shares your name and email — your headline and About still need pasting in.'
      );
    }
  }, [boot.fromLink, boot.auth, toast]);

  /* ------------------------------------------------------------ autosave --- */
  // Debounced so a fast typist triggers one write per pause, not one per key.
  useDebouncedEffect(profile, (value) => writeJson(STORAGE_KEYS.profile, value));
  useEffect(() => writeJson(STORAGE_KEYS.mode, mode), [mode]);

  /* ------------------------------------------------------ optional OAuth --- */
  useEffect(() => {
    const controller = new AbortController();
    void fetchBackendConfig(controller.signal).then(setBackend);
    return () => controller.abort();
  }, []);

  /* ------------------------------------------------------------ analysis --- */
  // Held one beat behind the keystrokes so the score is not jittering mid-word.
  // `analyze` itself is fast; this is about readability, not performance.
  const settled = useDebouncedValue(profile, 300);
  const report = useMemo(
    () => analyze(settled, mode, { now: new Date().toISOString() }),
    [settled, mode]
  );

  const started = hasContent(profile);

  const jumpToSection = (section: SectionId) => {
    const target = document.getElementById(`section-${section}`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Move focus as well as the viewport, or a keyboard user's next Tab lands
    // back where they were rather than in the section they just asked for.
    const field = target.querySelector<HTMLElement>('input, textarea, select');
    field?.focus({ preventScroll: true });
  };

  const clearData = () => {
    clearAll();
    setProfile(createEmptyProfile());
    toast.success('Cleared. Nothing of yours is left in this browser.');
  };

  return (
    <div className="min-h-dvh bg-page text-strong">
      <a
        href="#analyser"
        className="sr-only-live focus-visible:not-sr-only focus-visible:absolute focus-visible:top-4 focus-visible:left-4 focus-visible:z-50 focus-visible:rounded-field focus-visible:bg-card focus-visible:px-4 focus-visible:py-2"
      >
        Skip to the analyser
      </a>

      <SiteHeader
        signedInAs={signedInAs}
        report={started ? report : null}
        onJumpToSection={jumpToSection}
      />

      <main>
        {started ? null : <Hero onImport={() => setImportOpen(true)} backend={backend} />}

        <div
          id="analyser"
          className="mx-auto grid max-w-6xl gap-6 px-4 pt-8 pb-16 lg:grid-cols-[minmax(0,1fr)_23rem] lg:items-start lg:gap-8"
        >
          <div className="min-w-0 space-y-5">
            {started ? (
              <div className="no-print flex flex-wrap items-center gap-2">
                <Button variant="secondary" icon={<FileUp />} onClick={() => setImportOpen(true)}>
                  Import LinkedIn PDF
                </Button>
              </div>
            ) : null}

            <ProfileForm
              profile={profile}
              mode={mode}
              report={report}
              onChange={setProfile}
              onModeChange={setMode}
            />
          </div>

          {/* The score column is sticky on desktop so the number stays visible
              while you edit the thing it is scoring — that feedback loop is the
              entire point of the tool. On mobile it simply sits underneath, which
              is what the header's score chip exists to compensate for.

              `tabIndex={-1}` is not decoration: the chip is a link to `#report`,
              and without a focusable target the browser scrolls here but leaves
              focus behind in the header, so the next Tab goes to the section
              strip rather than into the report. */}
          <div id="report" tabIndex={-1} className="min-w-0 lg:sticky lg:top-28">
            {started ? (
              <ReportPanel
                report={report}
                profile={profile}
                onJumpToSection={jumpToSection}
                onClearData={clearData}
              />
            ) : (
              <EmptyScorePanel />
            )}
          </div>
        </div>

        <HowItWorks />
      </main>

      <SiteFooter />

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApply={(fields) => {
          setProfile((current) => ({ ...current, ...fields }));
          toast.success('Imported. Check each field before you trust the score.');
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Chrome                                                                     */
/* -------------------------------------------------------------------------- */

function SiteHeader({
  signedInAs,
  report,
  onJumpToSection,
}: {
  signedInAs: string | null;
  /**
   * `null` until there is something to score.
   *
   * The score chip and the section strip both only make sense once the form has
   * content: five pills reading zero, above a page that is still the pitch, is a
   * progress bar for work nobody has started. So the header is one 64px bar on
   * arrival and grows a second one the moment you type — which also means the
   * hero gets the top of the screen to itself.
   */
  report: Report | null;
  onJumpToSection: (section: SectionId) => void;
}) {
  return (
    <header className="no-print sticky top-0 z-30 border-b border-line bg-page/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4">
        <a href="#analyser" className="group flex items-center gap-2.5 no-underline">
          <span
            aria-hidden="true"
            className="grid size-9 place-items-center rounded-field text-white shadow-glow transition-transform duration-200 group-hover:scale-105"
            style={{ backgroundImage: 'var(--gradient-cta)' }}
          >
            <Sparkles className="size-4.5" />
          </span>
          {/* `whitespace-nowrap` because the wordmark is the first thing to give
              way when the header runs out of room: at 390px it was wrapping to
              two lines and pushing the header to 65px tall. Better to let the
              nav shrink than to break the name in half. */}
          <span className="font-display font-bold tracking-tight whitespace-nowrap text-strong">
            {APP_NAME}
          </span>
        </a>

        <nav className="flex items-center gap-2" aria-label="Site">
          {signedInAs ? (
            <span className="hidden rounded-pill bg-sunken px-2.5 py-1 text-xs text-muted md:inline">
              Signed in as {signedInAs}
            </span>
          ) : null}
          {report ? <ScoreChip score={report.overall} grade={report.grade} /> : null}
          <a
            href="#how-it-works"
            className="hidden rounded-pill px-3 py-1.5 text-sm font-medium text-muted no-underline hover:bg-sunken hover:text-strong sm:inline-block"
          >
            How it works
          </a>
          <ThemeToggle />
          <ButtonLink
            href={REPO_URL}
            target="_blank"
            rel="noreferrer noopener"
            size="sm"
            icon={<Code />}
          >
            {/* Two labels, and which one is *visible* flips at `sm`. Below it the
                link is icon-only and the name comes from the hidden span; above
                it the short visible word is the name. Both states keep the
                accessible name equal to something a voice-control user would
                actually say, and neither leaves the icon unlabelled. */}
            <span className="hidden sm:inline">GitHub</span>
            <span className="sr-only-live sm:hidden">Source on GitHub</span>
          </ButtonLink>
        </nav>
      </div>

      {report ? <SectionNav sections={report.sections} onJump={onJumpToSection} /> : null}
    </header>
  );
}

/**
 * The out-of-focus colour washes behind the hero.
 *
 * `isolate` on the section plus `-z-10` here keeps them behind the text without
 * putting them behind the page background. They drift on a 14–22 second cycle,
 * which is slow enough to register as atmosphere rather than as something moving
 * while you are trying to read — and the global reduced-motion rule stops them
 * outright for anyone who has asked for that.
 */
function HeroBlobs() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <span className="blob top-[-6rem] left-[-4rem] size-72 animate-float bg-brand-400" />
      <span className="blob top-[2rem] right-[-6rem] size-80 animate-float-slow bg-hype-400" />
      <span className="blob bottom-[-8rem] left-[30%] size-64 animate-float-slow bg-accent-400" />
    </div>
  );
}

function Hero({ onImport, backend }: { onImport: () => void; backend: BackendConfig }) {
  return (
    <section className="relative isolate mx-auto max-w-6xl px-4 pt-10 pb-4 sm:pt-16">
      <HeroBlobs />

      <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_26rem] lg:gap-12">
        <div>
          <p className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-card/70 px-3 py-1 text-xs font-semibold text-muted backdrop-blur">
            <ShieldCheck
              aria-hidden="true"
              className="size-3.5 text-success-600 dark:text-success-400"
            />
            Free, open source, no account
          </p>

          <h1 className="mt-4 text-display font-bold tracking-tight text-strong">
            Find out what your LinkedIn profile is{' '}
            <span className="gradient-text">actually saying</span>.
          </h1>

          <p className="mt-4 text-lg leading-relaxed text-muted">
            Paste your headline, About, and experience below. You will get a score, the specific
            reasons behind it, and rewrites you can copy — checked against the same things a
            recruiter skims for in six seconds.
          </p>

          {/* This is load-bearing, not marketing. The reason someone will paste
              their unpublished CV into a stranger's website is that they can see
              it never leaves the tab — so it is stated before the first field. */}
          <p className="mt-5 flex items-start gap-2.5 rounded-card border border-success-200 bg-success-50 p-4 text-sm text-success-700 dark:border-success-600 dark:bg-success-950 dark:text-success-100">
            <ShieldCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <span>
              <strong className="font-semibold">Everything runs in your browser.</strong> Nothing is
              uploaded, there is no server, and no account is needed. Your draft is saved only to
              this browser, and “Clear my data” removes it for good.
            </span>
          </p>

          <div className="mt-6 flex flex-wrap gap-2.5">
            <Button
              size="lg"
              icon={<Sparkles />}
              onClick={() =>
                document
                  .getElementById('section-headline')
                  ?.querySelector<HTMLElement>('input, textarea')
                  ?.focus()
              }
            >
              Start with my headline
            </Button>
            <Button size="lg" variant="secondary" icon={<FileUp />} onClick={onImport}>
              Import LinkedIn PDF
            </Button>
            {/* Rendered only when a backend answered and reported working OAuth.
                Sign-in is cosmetic by necessity: LinkedIn's OIDC scopes return
                name, picture, and email — never the headline or About — so it is
                labelled honestly rather than implied to auto-fill the form. */}
            {backend.oauthEnabled && backend.authUrl ? (
              <ButtonLink size="lg" href={backend.authUrl} icon={<LogIn />}>
                Sign in with LinkedIn
              </ButtonLink>
            ) : null}
          </div>
        </div>

        {/* Hidden below `lg` rather than stacked: on a phone it would push the
            first real field a full screen further down to show a picture of one. */}
        <div className="hidden lg:block">
          <HeroPreview />
        </div>
      </div>
    </section>
  );
}

function EmptyScorePanel() {
  return (
    <Card>
      <CardBody className="space-y-3 text-center">
        <span
          aria-hidden="true"
          className="mx-auto grid size-14 place-items-center rounded-pill text-white shadow-glow"
          style={{ backgroundImage: 'var(--gradient-cta)' }}
        >
          <Sparkles className="size-6" />
        </span>
        <h2 className="text-base font-semibold text-strong">Your score appears here</h2>
        <p className="text-sm text-muted">
          Fill in one field and it starts scoring straight away — no “analyse” button to press. We
          show a score only once there is something real to judge, because 0 out of 100 for an empty
          form tells you nothing.
        </p>
      </CardBody>
    </Card>
  );
}

const STEPS = [
  {
    title: 'Paste, or import your PDF',
    body: 'Copy each section straight off your profile. Or use LinkedIn’s own More → Save to PDF export and we read it here in the browser.',
  },
  {
    title: 'Pick who it is for',
    body: 'A student and a hiring manager are judged on different things, so the audience you choose reweights the whole rubric instead of just changing the wording.',
  },
  {
    title: 'Fix what actually matters',
    body: 'Every finding says why it costs you something, not just what to change. The top three are ranked by how much they move your score.',
  },
  {
    title: 'Copy the rewrites',
    body: 'Scaffolds, not fabrications. Any number we do not know is left as a highlighted [placeholder] for you to fill in — we will never invent a metric for you.',
  },
];

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      aria-labelledby="how-it-works-title"
      className="border-t border-line bg-sunken"
    >
      <div className="mx-auto max-w-6xl px-4 py-14">
        <h2 id="how-it-works-title" className="text-3xl font-bold tracking-tight text-strong">
          How it <span className="gradient-text">works</span>
        </h2>
        <p className="mt-2 max-w-2xl text-muted">
          No black box and no AI guessing at your career. It is a set of readable rules — you can
          open any one of them on GitHub and see exactly what it checks and why.
        </p>

        <ol className="mt-8 grid gap-4 sm:grid-cols-2">
          {STEPS.map((step, index) => (
            <li
              key={step.title}
              className="hover-lift rounded-card border border-line bg-card p-5 shadow-card"
            >
              <span
                aria-hidden="true"
                className="grid size-9 place-items-center rounded-field text-sm font-bold text-white shadow-glow"
                style={{ backgroundImage: 'var(--gradient-cta)' }}
              >
                {index + 1}
              </span>
              <h3 className="mt-3.5 font-semibold text-strong">{step.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="no-print border-t border-line">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 text-sm text-muted sm:flex-row sm:items-center sm:justify-between">
        <p>
          {APP_NAME} — MIT licensed.{' '}
          <a
            href={REPO_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="font-medium text-brand-600 underline decoration-brand-300 decoration-2 underline-offset-2 hover:text-hype-600 dark:text-brand-300 dark:decoration-brand-500 dark:hover:text-hype-400"
          >
            Contributions welcome
          </a>
          .{' '}
          {/* The header's copy of this link is hidden below 640px, where the
              score chip and the section strip need the room more. Repeating it
              here is what keeps the explanation reachable on a phone without a
              disclosure menu whose only contents would be one link. */}
          <a
            href="#how-it-works"
            className="font-medium text-brand-600 underline decoration-brand-300 decoration-2 underline-offset-2 hover:text-hype-600 dark:text-brand-300 dark:decoration-brand-500 dark:hover:text-hype-400"
          >
            How it works
          </a>
          .
        </p>
        <p className="text-xs text-subtle">{DISCLAIMER}</p>
      </div>
    </footer>
  );
}
